import { useState } from "react";
import { SETUP_LINK_ALLOWANCE, type LinkDraft } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { keys, useAction } from "../../lib/queries";
import type { UpsertResult } from "../../lib/types";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { useToast } from "../../ui/Feedback";
import { Field, Input } from "../../ui/Form";
import styles from "./LinksTab.module.css";

interface Page {
  url: string;
  title: string;
  description: string;
  image: string | null;
  sections: { id: string; title: string }[];
  error?: string;
}

const BATCH = 10;

/**
 * The first referral list for a website Navibiz did not build (catalog G:
 * "daftar awal disusun dari sitemap dan halaman klien, sampai 100 rujukan").
 * Three steps: read the sitemap, read the chosen pages (ten per request, to
 * stay inside a Worker's subrequest limit), then pick pages and the id'd
 * sections found on them.
 */
export function SitemapDialog({ projectId, websiteUrl, open, onClose }: { projectId: string; websiteUrl: string | null; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [sitemapUrl, setSitemapUrl] = useState(websiteUrl ? `${websiteUrl.replace(/\/$/, "")}/sitemap.xml` : "");
  const [urls, setUrls] = useState<string[] | null>(null);
  const [chosenUrls, setChosenUrls] = useState<Set<string>>(new Set());
  const [pages, setPages] = useState<Page[] | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const importLinks = useAction((links: LinkDraft[]) => api.post<UpsertResult>(`/projects/${projectId}/links/import`, { mode: "append", source: "sitemap", links }), [
    keys.links(projectId),
    keys.project(projectId),
  ]);

  const reset = () => {
    setUrls(null);
    setPages(null);
    setPicked(new Set());
    setChosenUrls(new Set());
    setProgress(null);
    setError(null);
  };
  const close = () => {
    reset();
    onClose();
  };

  async function loadSitemap() {
    setError(null);
    try {
      const result = await api.post<{ urls: string[]; truncated: boolean }>(`/projects/${projectId}/links/sitemap`, { url: sitemapUrl });
      setUrls(result.urls);
      setChosenUrls(new Set(result.urls.slice(0, 40)));
      if (result.truncated) toast("Sitemap berisi lebih dari 500 halaman; yang tampil 500 pertama.");
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "Sitemap gagal dibaca.");
    }
  }

  async function readPages() {
    const list = [...chosenUrls];
    const read: Page[] = [];
    setProgress(0);
    for (let i = 0; i < list.length; i += BATCH) {
      const { pages: batch } = await api.post<{ pages: Page[] }>(`/projects/${projectId}/links/inspect`, { urls: list.slice(i, i + BATCH) });
      read.push(...batch);
      setProgress(Math.min(list.length, i + BATCH));
    }
    setPages(read);
    setPicked(new Set(read.filter((page) => !page.error).map((page) => page.url)));
    setProgress(null);
  }

  const drafts: LinkDraft[] = (pages ?? []).flatMap((page) => {
    const out: LinkDraft[] = [];
    if (picked.has(page.url)) {
      out.push({ key: `url:${page.url}`, kind: "page", title: page.title || page.url, url: page.url, summary: page.description, price: null, imageUrl: page.image, tags: [], active: true });
    }
    for (const section of page.sections) {
      const url = `${page.url.split("#")[0]}#${section.id}`;
      if (picked.has(url)) out.push({ key: `url:${url}`, kind: "section", title: section.title, url, summary: page.title ? `Bagian dari ${page.title}.` : "", price: null, imageUrl: null, tags: [], active: true });
    }
    return out;
  });
  const toggle = (key: string) => {
    const next = new Set(picked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setPicked(next);
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      width={760}
      title="Rujukan dari sitemap"
      description={`Untuk website luar: Navibiz menyusun daftar awal sampai ${SETUP_LINK_ALLOWANCE} rujukan, lalu klien mengelolanya sendiri.`}
      footer={
        pages ? (
          <>
            <Button variant="ghost" onClick={reset}>
              Mulai ulang
            </Button>
            <Button
              variant="primary"
              disabled={drafts.length === 0}
              loading={importLinks.isPending}
              onClick={async () => {
                try {
                  const result = await importLinks.mutateAsync(drafts);
                  toast(`${result.created} rujukan baru, ${result.updated} diperbarui${result.rejected.length ? `, ${result.rejected.length} ditolak` : ""}.`);
                  close();
                } catch (failure) {
                  toast(failure instanceof Error ? failure.message : "Impor gagal.", "critical");
                }
              }}
            >
              Impor {drafts.length} rujukan
            </Button>
          </>
        ) : urls ? (
          <>
            <Button variant="ghost" onClick={reset}>
              Kembali
            </Button>
            <Button variant="primary" disabled={chosenUrls.size === 0 || progress !== null} loading={progress !== null} onClick={readPages}>
              Baca {chosenUrls.size} halaman
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              Batal
            </Button>
            <Button variant="primary" disabled={!sitemapUrl} onClick={loadSitemap}>
              Baca sitemap
            </Button>
          </>
        )
      }
    >
      {!urls && (
        <Field label="Alamat sitemap" error={error} hint="Biasanya /sitemap.xml, /sitemap_index.xml (WordPress + Yoast), atau /wp-sitemap.xml.">
          {(id, describedBy) => <Input id={id} type="url" value={sitemapUrl} aria-describedby={describedBy} onChange={(event) => setSitemapUrl(event.target.value)} />}
        </Field>
      )}

      {urls && !pages && (
        <div className={styles.wizard}>
          <p>
            {urls.length} halaman ditemukan di domain yang diizinkan.{" "}
            {progress !== null ? `Membaca ${progress}/${chosenUrls.size}…` : "Pilih halaman yang layak dirujuk, lalu baca isinya."}
          </p>
          <div className={styles.wizardActions}>
            <Button size="sm" variant="ghost" onClick={() => setChosenUrls(new Set(urls.slice(0, SETUP_LINK_ALLOWANCE)))}>
              Pilih {Math.min(urls.length, SETUP_LINK_ALLOWANCE)} pertama
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setChosenUrls(new Set())}>
              Kosongkan
            </Button>
          </div>
          <ul className={styles.pickList}>
            {urls.map((url) => (
              <li key={url}>
                <label>
                  <input
                    type="checkbox"
                    checked={chosenUrls.has(url)}
                    onChange={() => {
                      const next = new Set(chosenUrls);
                      if (next.has(url)) next.delete(url);
                      else next.add(url);
                      setChosenUrls(next);
                    }}
                  />
                  <span>{url.replace(/^https?:\/\//, "")}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      {pages && (
        <div className={styles.wizard}>
          <p>
            Centang halaman dan bagian yang mau dirujuk. Bagian diambil dari elemen ber-id yang punya judul; halaman yang dirender JavaScript bisa tampil tanpa
            bagian, tambahkan manual bila perlu.
          </p>
          <ul className={styles.pickList}>
            {pages.map((page) => (
              <li key={page.url}>
                <label>
                  <input type="checkbox" disabled={!!page.error} checked={picked.has(page.url)} onChange={() => toggle(page.url)} />
                  <span>
                    <strong>{page.title || page.url}</strong>
                    <small>{page.error ? `Gagal dibaca: ${page.error}` : page.description || page.url}</small>
                  </span>
                </label>
                {page.sections.length > 0 && (
                  <ul>
                    {page.sections.map((section) => {
                      const key = `${page.url.split("#")[0]}#${section.id}`;
                      return (
                        <li key={key}>
                          <label>
                            <input type="checkbox" checked={picked.has(key)} onChange={() => toggle(key)} />
                            <span>
                              {section.title} <small>#{section.id}</small>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Dialog>
  );
}
